// Laser kiosk sheet map: which pallet / rack each piece on the sheet goes to.
// Pure: no React, no fetch. Tests: sheetMap.test.js.
//
// The map (GET /cut_sheets/{id}/nest_map, jobq-api nest_map.py) gives every piece on ONE sheet with its
// product_id and position. The kiosk's part destinations say where ALL the job's pieces of a part go, in
// fill order. Sheet N of a part that nests q per sheet is pieces (N-1)q .. Nq-1 of that order, so each
// sheet's pieces are shared out sheet by sheet, the first sheets filling the first destinations.
// Within a sheet the pieces are taken in columns (left to right, top to bottom in a column), so one
// destination's pieces sit together on the drawing.
import { destinationsInFillOrder, doNotAddReason, fillOrder } from "./kioskRules";

// Colours for the job's destinations, in fill order (high-contrast, colour-blind-safe first).
export const DEST_COLORS = [
  "#0072B2", "#E69F00", "#009E73", "#CC79A7", "#56B4E9", "#D55E00", "#F0E442", "#7F3C8D",
  "#11A579", "#3969AC", "#F2B701", "#E73F74",
];
const TONE = { owed: "#FF7F50", surplus: "#C8B568", blocked: "#D62828", none: "#5A6170" };

const COLUMN_IN = 6;   // pieces whose labels are within this many inches across read as one column

function upper(s) {
  return String(s || "").trim().toUpperCase();
}

// Pieces per sheet by product (upper case), from the map's own parts list.
export function piecesPerSheet(map) {
  const out = {};
  for (const r of (map && map.rows) || []) {
    const k = upper(r.product_id);
    if (k) out[k] = (out[k] || 0) + (Number(r.qty) || 0);
  }
  return out;
}

// A key that identifies a destination across parts (the same box gets the same colour).
export function destKey(d) {
  if (!d) return "none";
  if (d.kind === "packaging") return `box:${d.box_id ?? `${d.pallet_short_id}/${d.box_num}`}`;
  if (d.kind === "owed") return `owed:${d.owed_kind || ""}:${d.dest_so_id ?? d.dest_so_number ?? ""}`;
  return `${d.kind || "other"}`;
}

export function destLabel(d) {
  if (!d) return "Not on this job";
  if (d.kind === "packaging") {
    const container = d.is_pallet_direct ? "Stack" : "Box";
    const box = d.box_short_id || `${d.is_pallet_direct ? "S" : "B"}${d.box_num ?? ""}`;
    return `${d.pallet_short_id || `P${d.pallet_num ?? ""}`} · ${container} ${box}`;
  }
  if (d.kind === "owed") {
    return d.owed_kind === "bad_idea" ? "Bad Idea stock" : `Owed ${d.dest_so_number || "to an order"}: set aside`;
  }
  if (d.kind === "surplus") return "Surplus rack";
  return d.kind || "Other";
}

// Spatial order of a sheet's pieces: columns left to right, top to bottom inside a column.
export function spatialOrder(pieces) {
  return [...pieces].sort((a, b) => {
    const ca = Math.floor((Number(a.cx) || 0) / COLUMN_IN);
    const cb = Math.floor((Number(b.cx) || 0) / COLUMN_IN);
    return ca - cb || (Number(a.cy) || 0) - (Number(b.cy) || 0) || (Number(a.cx) || 0) - (Number(b.cx) || 0)
      || (a.i ?? 0) - (b.i ?? 0);
  });
}

// Every piece of sheet `sheetNo` (1-based) with where it goes.
// opts.sheetsPlanned (the task's sheet_required): a part whose destinations hold MORE pieces than its planned
// sheets make (an overrun, a second laser task on the job) can't be placed sheet by sheet: its pieces read
// "Can't place" instead of a guess.
// Returns {pieces: [{...piece, dest, key, color, pattern, seq}], legend: [{key, label, color, pattern, count, seq,
// of, beacon, blocked, dest}], perSheet, unknownProducts, cantPlace: [product_id], short: [{product, by}] (fewer
// places than pieces cut: a defect moved the boundaries), unchecked (no planned sheet count to check against)}.
export function assignSheet(map, parts, sheetNo, opts = {}) {
  const perSheet = piecesPerSheet(map);
  const byProduct = {};
  for (const p of parts || []) {
    const k = upper(p.product_id);
    if (k && !byProduct[k]) byProduct[k] = p;
  }
  const planned = Math.max(0, Math.floor(Number(opts.sheetsPlanned) || 0));

  const n = Math.max(1, Math.floor(Number(sheetNo) || 1));
  const groups = {};
  for (const pc of (map && map.pieces) || []) {
    const k = upper(pc.product_id);
    (groups[k] = groups[k] || []).push(pc);
  }
  const out = [];
  const legend = new Map();
  const unknown = new Set();
  const cantPlace = [];
  const short = [];          // [{product, by}]: fewer places than pieces cut (a defect): colours near a change may be off
  for (const [k, list] of Object.entries(groups)) {
    const part = byProduct[k];
    const ordered = spatialOrder(list);
    if (!part) unknown.add(k);
    const destTotal = part ? (part.destinations || []).reduce((a, d) => a + (Math.floor(Number(d.qty) || 0)), 0) : 0;
    if (part && planned && destTotal > 0 && destTotal < planned * (perSheet[k] || ordered.length)) {
      short.push({ product: k, by: planned * (perSheet[k] || ordered.length) - destTotal });
    }
    if (part && planned && destTotal > planned * (perSheet[k] || ordered.length)) {
      cantPlace.push(k);
      for (const pc of ordered) out.push({ ...pc, dest: null, key: "cant_place", color: TONE.none, pattern: null, seq: null });
      const e = legend.get("cant_place") || {
        key: "cant_place", color: TONE.none, pattern: null, count: 0, dest: null, seq: null, of: null, blocked: null,
        beacon: null, label: "Can't place: the job's pieces don't match its sheets (see the cards)",
      };
      e.count += ordered.length;
      legend.set("cant_place", e);
      continue;
    }
    // the job's pieces of this part, one entry per destination in fill order
    const seq = [];
    if (part) {
      const order = fillOrder(part.destinations);
      for (const d of destinationsInFillOrder(part.destinations)) {
        const q = Math.max(0, Math.floor(Number(d.qty) || 0));
        if (q) seq.push({ d, q, order: order.get(d) });
      }
    }
    const q = perSheet[k] || ordered.length;
    let skip = (n - 1) * q;
    let si = 0;
    while (si < seq.length && skip >= seq[si].q) { skip -= seq[si].q; si += 1; }
    let left = si < seq.length ? seq[si].q - skip : 0;
    for (const pc of ordered) {
      while (si < seq.length && left <= 0) { si += 1; left = si < seq.length ? seq[si].q : 0; }
      const hit = si < seq.length ? seq[si] : null;
      if (hit) left -= 1;
      const d = hit ? hit.d : null;
      const key = part ? (d ? destKey(d) : "unrouted") : "unknown";
      out.push({ ...pc, dest: d, key, seq: hit?.order?.seq ?? null });
      const e = legend.get(key) || {
        key, count: 0, dest: d, seq: hit?.order?.seq ?? null, of: hit?.order?.of ?? null,
        blocked: d ? doNotAddReason(d) : null,
        label: d ? destLabel(d) : part ? "Not routed yet" : "Not on this job",
        beacon: d && d.kind === "packaging" ? d.pallet_beacon_id || null : null,
      };
      e.count += 1;
      legend.set(key, e);
    }
  }
  const legendList = [...legend.values()].sort(
    (a, b) => (a.seq ?? Infinity) - (b.seq ?? Infinity) || (a.blocked ? 1 : 0) - (b.blocked ? 1 : 0)
      || a.label.localeCompare(b.label));
  // Colours for THIS sheet's destinations only, in legend order, so no two boxes on the sheet share one. Not a
  // pallet: a pattern too (DO NOT ADD cross-hatched red, Owed / Surplus striped), never only a colour.
  let next = 0;
  for (const e of legendList) {
    if (e.key === "cant_place") continue;
    const d = e.dest;
    if (!d) { e.color = TONE.none; e.pattern = null; continue; }
    if (e.blocked) { e.color = TONE.blocked; e.pattern = "cross"; continue; }
    if (d.kind === "packaging") { e.color = DEST_COLORS[next++ % DEST_COLORS.length]; e.pattern = null; continue; }
    e.color = TONE[d.kind] || TONE.none;
    e.pattern = "stripe";
  }
  const byKey = new Map(legendList.map((e) => [e.key, e]));
  for (const p of out) {
    if (p.color) continue;
    const e = byKey.get(p.key);
    p.color = e ? e.color : TONE.none;
    p.pattern = e ? e.pattern : null;
  }
  return { pieces: out, legend: legendList, perSheet, unknownProducts: [...unknown], cantPlace, short,
           unchecked: !planned };
}

// Add Sheet is pressed when a sheet goes ONTO the laser (Mike, 2026-10-26), so while the task runs the last sheet
// recorded is the one being cut and the one coming off is the one before it. Once the task is done (Completed, or
// the kiosk's post-complete view) the last sheet recorded is the one being unloaded.
function taskDone(task) {
  return !!task?.is_post_complete || String(task?.task_status || task?.status || "").toLowerCase() === "completed";
}

// The sheet to show first: the one being unloaded (sheet 1 while the first sheet is still on the laser). Never
// capped at the planned count: a 4th sheet on a 3-sheet task is sheet 4, and its pieces read "Not routed yet".
export function defaultSheet(task) {
  const cut = Math.floor(Number(task?.sheet_count) || 0);
  if (taskDone(task)) return Math.max(1, cut);
  return Math.max(1, cut - 1);
}

// What the operator is looking at: "coming off" (the sheet being unloaded), "on the laser" (being cut now), or
// null for any other sheet.
export function sheetRole(sheet, task) {
  const cut = Math.floor(Number(task?.sheet_count) || 0);
  if (!cut) return null;
  if (taskDone(task)) return sheet === cut ? "coming off" : null;
  if (sheet === cut) return "on the laser";
  if (sheet === cut - 1) return "coming off";
  return null;
}

// How far the stepper goes: the planned sheets, or every sheet recorded when more were cut.
export function lastSheet(task) {
  return Math.max(1, Math.floor(Number(task?.sheet_required) || 0), Math.floor(Number(task?.sheet_count) || 0));
}

// "Sheet 2 of 3 · coming off", or "Sheet 4 (3 planned)" past the plan.
export function sheetLabel(sheet, task) {
  const req = Math.max(0, Math.floor(Number(task?.sheet_required) || 0));
  const base = req && sheet > req ? `Sheet ${sheet} (${req} planned)` : `Sheet ${sheet} of ${Math.max(req, sheet)}`;
  const role = sheetRole(sheet, task);
  return role ? `${base} · ${role}` : base;
}

// Whether to colour the map at all, and why not (the API's flags: GET /cut_sheets/{id}/nest_map).
export function mapTrust(body) {
  if (!body) return { colour: false, why: null };
  if (body.colour === true) return { colour: true, why: null };
  if ((body.stale || []).length) return { colour: false, why: `the cut sheet changed since its PDF was read (${body.stale[0]})` };
  if ((body.mismatch || []).length) return { colour: false, why: "its PDF was attached although its parts differ" };
  const prob = (body.map && body.map.problems) || body.problems || [];
  return { colour: false, why: prob.length ? `the PDF couldn't be read cleanly (${prob[0]})` : "it couldn't be checked" };
}
